import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { When } from 'react-if';
import { TextArea as ZusTextArea } from '@zep-us/zus';
import type { AttachmentState, GenerateStatus } from '@/shared/features/ai-quiz-generation/types';
import { cn } from '@/utils';
import { PromptTextArea } from '@/views/build/features/ai-prompt-input/components/PromptTextArea';
import { useAiFileDroppable } from '../hooks/useAiFileDroppable';

// 1. Context Definition
/**
 * AttachmentContext 값 타입 -- 모든 첨부 로직(파일 DnD + YouTube)을 통합
 *
 * AttachmentProvider가 제공하는 값들로, 자식 컴포넌트에서
 * useAttachment() hook을 통해 접근합니다.
 */
export interface AttachmentContextValue {
  // --- 상태 ---
  /** 현재 첨부 상태 (file/youtube/none) */
  attachment: AttachmentState;
  /** 생성 상태 (generating/regenerating 시 버튼 비활성화) */
  generateStatus: GenerateStatus;

  // --- 파일 핸들러 ---
  /** 파일 첨부 (DnD 또는 파일 선택기). beforeAttach 가드 + 유효성 검사 실행. */
  handleAttachFile: (file: File) => Promise<void>;
  /** 네이티브 파일 선택기 대화상자 열기. beforeAttach 가드 실행. */
  openFilePicker: () => void;

  // --- YouTube 핸들러 ---
  /** YouTube URL 입력 대화상자 열기. AutoTrigger 모달에서는 업셀링을 대신 열음. */
  handleAttachYoutube: () => Promise<void>;

  // --- Website 핸들러 ---
  /** Website URL 입력 대화상자 열기. */
  handleAttachWebsite: () => Promise<void>;

  // --- 공통 핸들러 ---
  /** 현재 첨부 제거 ({type: 'none', data: null}로 리셋). */
  handleDetach: () => void;

  // --- UI 상태 ---
  /** 파일이 드롭 영역 위로 드래그 중일 때 true. 버튼을 숨겨야 함. */
  isDragActive: boolean;
}
const AttachmentContext = createContext<AttachmentContextValue | null>(null);

/**
 * AttachmentContext를 소비하는 hook.
 *
 * 반드시 `AttachmentProvider` 내부에서 사용해야 합니다.
 * 외부에서 사용 시 에러를 throw합니다.
 *
 * @example
 * ```tsx
 * const PromptInput = () => {
 *   const { openFilePicker, isDragActive, attachment } = useAttachment();
 *   return (
 *     <div className={isDragActive ? 'hidden' : ''}>
 *       <button onClick={openFilePicker}>파일 첨부</button>
 *     </div>
 *   );
 * };
 * ```
 */
export const useAttachment = () => {
  const context = useContext(AttachmentContext);
  if (!context) {
    throw new Error('useAttachment must be used within AttachmentProvider');
  }
  return context;
};

// 2. Provider Component
/**
 * AttachmentProvider props
 */
export interface AttachmentProviderProps {
  /** 현재 attachment 상태 (file/youtube/none) */
  attachment: AttachmentState;

  /** 파일 첨부 핸들러 */
  onAttachFile: (file: File) => Promise<void>;

  /** YouTube 첨부 핸들러. useAiBuild에서 가져오거나 업셀링 가드로 래핑. */
  onAttachYoutube: () => Promise<void>;

  /** Website 첨부 핸들러. */
  onAttachWebsite: () => Promise<void>;

  /** Attachment 제거 핸들러 */
  onDetach: () => void;

  /** 생성 상태 (generating/regenerating 시 버튼 비활성화) */
  generateStatus: GenerateStatus;

  /**
   * 파일 첨부 전 guard. false 반환 시 첨부 중단 (validation 전에 실행됨).
   * DnD drop과 file picker button 모두에 적용됩니다.
   *
   * @example
   * // upselling guard
   * beforeAttach={() => {
   *   upsellingModal.open({ initialPlanType: 'BASIC' });
   *   return false;
   * }}
   */
  beforeAttach?: () => boolean | Promise<boolean>;

  /**
   * 마운트 시 자동으로 file picker를 열 것인지.
   * ref guard가 있어 React strict mode에서도 1회만 실행됩니다.
   */
  autoOpenFilePicker?: boolean;

  /** DnD 영역 내부에 렌더링되는 자식 요소. */
  children: React.ReactNode;

  /** 컨테이너에 추가할 className. */
  className?: string;
}

/**
 * AttachmentProvider -- 모든 첨부 로직(파일 DnD + YouTube)의 통합 provider
 *
 * DnD 로직은 AiDropZone.Root에서 직접 흡수됩니다:
 * - useAiFileDroppable hook 사용
 * - getRootProps, isDragActive, inputRef 등 동일하게 사용
 * - UI 렌더링 구조도 동일 (ZUS TextArea.InputWrapper + hidden input + FileOverIndicator)
 *
 * Context를 통해 자식 컴포넌트에 모든 첨부 관련 핸들러와 상태를 제공합니다:
 * - 파일 관련: openFilePicker, handleAttachFile, isDragActive
 * - YouTube 관련: handleAttachYoutube
 * - 공통: handleDetach, attachment, generateStatus
 *
 * @example
 * ```tsx
 * <AttachmentProvider
 *   attachment={attachment.state}
 *   onAttachFile={attachment.handleAttachFile}
 *   onAttachYoutube={handleYoutubeWithUpselling}
 *   onAttachWebsite={handleWebsite}
 *   onDetach={attachment.handleDetach}
 *   generateStatus={generation.status}
 *   beforeAttach={beforeFileAttach}
 * >
 *   <PromptInput
 *     prompt={form.textPrompt}
 *     onChangePrompt={form.onChangePrompt}
 *   />
 * </AttachmentProvider>
 * ```
 */
export const AttachmentProvider = ({
  attachment,
  onAttachFile,
  onAttachYoutube,
  onAttachWebsite,
  onDetach,
  generateStatus,
  beforeAttach,
  autoOpenFilePicker,
  children,
  className
}: AttachmentProviderProps) => {
  // ============================================
  // DnD 로직 (AiDropZone에서 흡수)
  // ============================================

  // 1. useAiFileDroppable hook 사용 (동일)
  const {
    getRootProps,
    isDragActive,
    inputProps,
    inputRef
  } = useAiFileDroppable({
    onAttachComplete: (file: File) => {
      void onAttachFile(file);
    },
    beforeAttach
  });

  // 2. openFilePicker 함수 생성 (동일)
  const openFilePicker = useCallback(() => {
    void (async () => {
      if (beforeAttach) {
        const shouldOpen = await beforeAttach();
        if (!shouldOpen) return;
      }
      inputRef.current?.click();
    })();
  }, [inputRef, beforeAttach]);

  // 3. autoOpenFilePicker ref guard -- React strict mode에서 이중 실행 방지
  const hasAutoOpened = useRef(false);
  useEffect(() => {
    if (autoOpenFilePicker && !hasAutoOpened.current) {
      hasAutoOpened.current = true;
      inputRef.current?.click();
    }
  }, [autoOpenFilePicker, inputRef]);

  // ============================================
  // 추가 로직 (YouTube 등 통합)
  // ============================================

  // 4. hasAttachment 계산 (내부)
  const hasAttachment = attachment.type !== 'none';

  // 5. 통합 Context 생성
  const contextValue = useMemo<AttachmentContextValue>(() => ({
    // DnD 관련 (기존 AiDropZone)
    openFilePicker: () => {
      void openFilePicker();
    },
    isDragActive,
    // Attachment 상태 및 핸들러 (새로 추가)
    attachment,
    handleAttachFile: onAttachFile,
    handleAttachYoutube: onAttachYoutube,
    handleAttachWebsite: onAttachWebsite,
    handleDetach: onDetach,
    // UI 상태
    generateStatus
  }), [openFilePicker, isDragActive, attachment, onAttachFile, onAttachYoutube, onAttachWebsite, onDetach, generateStatus]);

  // ============================================
  // UI 렌더링 (AiDropZone과 동일)
  // ============================================

  return <AttachmentContext.Provider value={contextValue} data-sentry-element="unknown" data-sentry-component="AttachmentProvider" data-sentry-source-file="AttachmentProvider.tsx">
      <div {...getRootProps()} className={cn('relative grow gap-xs h-[173px] flex', className)}>
        <ZusTextArea.InputWrapper intent="strong" className={cn('h-full grow flex-col gap-xs',
      // dragActive/hasAttachment 시 ZUS focus-within과 동일한 ring 강제 적용
      (isDragActive || hasAttachment) && 'border-transparent ring-2 ring-inset ring-strong-default')} data-sentry-element="unknown" data-sentry-source-file="AttachmentProvider.tsx">
          <input {...inputProps} type="file" hidden ref={inputRef} />
          {children}
          <When condition={isDragActive} data-sentry-element="When" data-sentry-source-file="AttachmentProvider.tsx">
            <PromptTextArea.FileOverIndicator data-sentry-element="unknown" data-sentry-source-file="AttachmentProvider.tsx" />
          </When>
        </ZusTextArea.InputWrapper>
      </div>
    </AttachmentContext.Provider>;
};